import React, { useEffect, useMemo, useState } from "react";
import {
  beregnManedskostnad,
  summerManed,
  MONTH_NAMES_LONN,
} from "./lonnskostUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function fmtKr(n) {
  const v = Number(n) || 0;
  return `${Math.round(v).toLocaleString("nb-NO")} kr`;
}

function toDateInputValue(d) {
  if (!d) return "";
  return String(d).slice(0, 10);
}

function InnstillingerPanel({ innstilling, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [agp, setAgp] = useState(innstilling.arbeidsgiveravgiftProsent);
  const [feriepenger, setFeriepenger] = useState(innstilling.feriepengerProsent);
  const [otp, setOtp] = useState(innstilling.otpProsent);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAgp(innstilling.arbeidsgiveravgiftProsent);
    setFeriepenger(innstilling.feriepengerProsent);
    setOtp(innstilling.otpProsent);
  }, [innstilling]);

  async function handleSave() {
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_URL}/lonnskost/innstillinger`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          arbeidsgiveravgiftProsent: agp,
          feriepengerProsent: feriepenger,
          otpProsent: otp,
        }),
      });
      if (!r.ok) throw new Error();
      onSaved(await r.json());
      setEditing(false);
    } catch {
      alert("Kunne ikke lagre satser.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-indigo-800">Satser brukt i beregningen</p>
          {!editing ? (
            <p className="text-sm text-indigo-900 mt-1">
              Arbeidsgiveravgift {innstilling.arbeidsgiveravgiftProsent}% · Feriepenger {innstilling.feriepengerProsent}% · OTP {innstilling.otpProsent}%
            </p>
          ) : (
            <div className="flex flex-wrap gap-3 mt-2">
              <label className="text-xs text-indigo-800">
                Arbeidsgiveravgift %
                <input type="number" step="0.1" value={agp} onChange={(e) => setAgp(e.target.value)} className="block mt-0.5 p-1.5 border border-indigo-300 rounded w-24 text-sm" />
              </label>
              <label className="text-xs text-indigo-800">
                Feriepenger %
                <input type="number" step="0.1" value={feriepenger} onChange={(e) => setFeriepenger(e.target.value)} className="block mt-0.5 p-1.5 border border-indigo-300 rounded w-24 text-sm" />
              </label>
              <label className="text-xs text-indigo-800">
                OTP %
                <input type="number" step="0.1" value={otp} onChange={(e) => setOtp(e.target.value)} className="block mt-0.5 p-1.5 border border-indigo-300 rounded w-24 text-sm" />
              </label>
            </div>
          )}
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="text-xs text-indigo-700 hover:text-indigo-900 underline shrink-0">Rediger</button>
        ) : (
          <div className="flex gap-2 shrink-0">
            <button onClick={handleSave} disabled={saving} className="px-3 py-1 rounded bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-60">
              {saving ? "Lagrer…" : "Lagre"}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-1 rounded bg-white border border-indigo-300 text-indigo-700 text-xs font-medium hover:bg-indigo-100">
              Avbryt
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StillingsprosentHistorikkEditor({ historikk, onChange }) {
  const [nyDato, setNyDato] = useState("");
  const [nyProsent, setNyProsent] = useState("");

  const sortert = useMemo(
    () => [...(historikk || [])].sort((a, b) => new Date(a.gjeldendeFra) - new Date(b.gjeldendeFra)),
    [historikk]
  );

  function leggTil() {
    if (!nyDato || nyProsent === "") return;
    const oppdatert = [...(historikk || []), { gjeldendeFra: nyDato, stillingsprosent: Number(nyProsent) }];
    onChange(oppdatert);
    setNyDato("");
    setNyProsent("");
  }

  function fjern(idx) {
    const oppdatert = sortert.filter((_, i) => i !== idx);
    onChange(oppdatert);
  }

  return (
    <div className="mt-2">
      <p className="text-xs font-medium text-gray-500 mb-1">Stillingsprosent over tid</p>
      {sortert.length === 0 ? (
        <p className="text-xs text-gray-400 mb-1">Ingen stillingsprosent registrert ennå.</p>
      ) : (
        <ul className="text-xs text-gray-700 space-y-0.5 mb-1.5">
          {sortert.map((e, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <span>{toDateInputValue(e.gjeldendeFra)}: {e.stillingsprosent}%</span>
              <button type="button" onClick={() => fjern(idx)} className="text-red-500 hover:text-red-700 underline">fjern</button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input type="date" value={nyDato} onChange={(e) => setNyDato(e.target.value)} className="p-1.5 border border-gray-300 rounded text-xs" />
        <input type="number" placeholder="%" value={nyProsent} onChange={(e) => setNyProsent(e.target.value)} className="p-1.5 border border-gray-300 rounded text-xs w-16" />
        <button type="button" onClick={leggTil} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-xs font-medium">+ Legg til endring</button>
      </div>
    </div>
  );
}

function NyAnsattForm({ onCreated, onCancel }) {
  const [navn, setNavn] = useState("");
  const [lonnstype, setLonnstype] = useState("fast");
  const [startdato, setStartdato] = useState("");
  const [manedslonn, setManedslonn] = useState("");
  const [startProsent, setStartProsent] = useState("100");
  const [timelonn, setTimelonn] = useState("");
  const [timerPerManed, setTimerPerManed] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!navn.trim()) return alert("Navn er påkrevd.");
    if (!startdato) return alert("Startdato er påkrevd.");
    setSaving(true);
    try {
      const body = {
        navn,
        lonnstype,
        startdato,
        ...(lonnstype === "fast"
          ? {
              manedslonnVed100Prosent: Number(manedslonn) || 0,
              stillingsprosentHistorikk: [{ gjeldendeFra: startdato, stillingsprosent: Number(startProsent) || 0 }],
            }
          : {
              timelonn: Number(timelonn) || 0,
              forventedeTimerPerManed: Number(timerPerManed) || 0,
            }),
      };
      const r = await fetch(`${API_BASE_URL}/lonnskost/ansatte`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      onCreated(await r.json());
    } catch {
      alert("Kunne ikke opprette lønnsansatt.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Ny lønnsansatt</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="Navn"
          value={navn}
          onChange={(e) => setNavn(e.target.value)}
          className="p-2 border border-gray-300 rounded-md"
        />
        <select value={lonnstype} onChange={(e) => setLonnstype(e.target.value)} className="p-2 border border-gray-300 rounded-md">
          <option value="fast">Fastlønn</option>
          <option value="timelonn">Timelønn</option>
        </select>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Startdato</label>
          <input type="date" value={startdato} onChange={(e) => setStartdato(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full" />
        </div>
      </div>

      {lonnstype === "fast" ? (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Månedslønn ved 100% (brutto)</label>
            <input type="number" placeholder="45000" value={manedslonn} onChange={(e) => setManedslonn(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Stillingsprosent fra start</label>
            <input type="number" placeholder="100" value={startProsent} onChange={(e) => setStartProsent(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full" />
          </div>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Timelønn (kr/t)</label>
            <input type="number" value={timelonn} onChange={(e) => setTimelonn(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Forventede timer pr. måned</label>
            <input type="number" value={timerPerManed} onChange={(e) => setTimerPerManed(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full" />
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-3">
        <button type="submit" disabled={saving} className="px-4 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
          {saving ? "Lagrer…" : "Opprett"}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-1.5 rounded-md bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200">
          Avbryt
        </button>
      </div>
    </form>
  );
}

function AnsattCard({ ansatt, year, monthIndex, innstilling, onUpdated, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [navn, setNavn] = useState(ansatt.navn);
  const [manedslonn, setManedslonn] = useState(ansatt.manedslonnVed100Prosent);
  const [historikk, setHistorikk] = useState(ansatt.stillingsprosentHistorikk || []);
  const [timelonn, setTimelonn] = useState(ansatt.timelonn);
  const [timerPerManed, setTimerPerManed] = useState(ansatt.forventedeTimerPerManed);
  const [sluttdato, setSluttdato] = useState(toDateInputValue(ansatt.sluttdato));
  const [saving, setSaving] = useState(false);

  const kostnadDenneManeden = beregnManedskostnad(ansatt, year, monthIndex, innstilling);

  async function lagre(patch) {
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE_URL}/lonnskost/ansatte/${ansatt._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error();
      onUpdated(await r.json());
    } catch {
      alert("Kunne ikke lagre endring.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    await lagre({
      navn,
      sluttdato: sluttdato || null,
      ...(ansatt.lonnstype === "fast"
        ? { manedslonnVed100Prosent: Number(manedslonn) || 0, stillingsprosentHistorikk: historikk }
        : { timelonn: Number(timelonn) || 0, forventedeTimerPerManed: Number(timerPerManed) || 0 }),
    });
    setEditing(false);
  }

  async function handleDelete() {
    if (!window.confirm(`Slette ${ansatt.navn} fra lønnskost?`)) return;
    try {
      const r = await fetch(`${API_BASE_URL}/lonnskost/ansatte/${ansatt._id}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      onDeleted(ansatt._id);
    } catch {
      alert("Kunne ikke slette.");
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900">{ansatt.navn}</p>
          <p className="text-xs text-gray-500">
            {ansatt.lonnstype === "fast" ? "Fastlønn" : "Timelønn"}
            {ansatt.sluttdato ? ` · Sluttet ${toDateInputValue(ansatt.sluttdato)}` : ""}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-400">{MONTH_NAMES_LONN[monthIndex]} {year}</p>
          <p className="font-semibold text-gray-900">{fmtKr(kostnadDenneManeden.total)}</p>
        </div>
      </div>

      {!editing ? (
        <div className="mt-2 flex gap-2">
          <button onClick={() => setEditing(true)} className="text-xs text-indigo-600 hover:text-indigo-800 underline">Rediger</button>
          <button onClick={handleDelete} className="text-xs text-red-500 hover:text-red-700 underline">Slett</button>
        </div>
      ) : (
        <div className="mt-3 space-y-2 border-t pt-3">
          <input type="text" value={navn} onChange={(e) => setNavn(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full text-sm" placeholder="Navn" />

          {ansatt.lonnstype === "fast" ? (
            <>
              <label className="text-xs font-medium text-gray-500 block">
                Månedslønn ved 100% (brutto)
                <input type="number" value={manedslonn} onChange={(e) => setManedslonn(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full text-sm mt-0.5" />
              </label>
              <StillingsprosentHistorikkEditor historikk={historikk} onChange={setHistorikk} />
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs font-medium text-gray-500 block">
                Timelønn (kr/t)
                <input type="number" value={timelonn} onChange={(e) => setTimelonn(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full text-sm mt-0.5" />
              </label>
              <label className="text-xs font-medium text-gray-500 block">
                Timer/mnd
                <input type="number" value={timerPerManed} onChange={(e) => setTimerPerManed(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full text-sm mt-0.5" />
              </label>
            </div>
          )}

          <label className="text-xs font-medium text-gray-500 block">
            Sluttdato (valgfritt)
            <input type="date" value={sluttdato} onChange={(e) => setSluttdato(e.target.value)} className="p-2 border border-gray-300 rounded-md w-full text-sm mt-0.5" />
          </label>

          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} disabled={saving} className="px-3 py-1 rounded bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-60">
              {saving ? "Lagrer…" : "Lagre"}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200">
              Avbryt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkshopLonnskostTab() {
  const [ansatte, setAnsatte] = useState([]);
  const [innstilling, setInnstilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [detaljMonth, setDetaljMonth] = useState(new Date().getMonth());

  useEffect(() => {
    async function hent() {
      try {
        const [ansatteRes, innstillingRes] = await Promise.all([
          fetch(`${API_BASE_URL}/lonnskost/ansatte`),
          fetch(`${API_BASE_URL}/lonnskost/innstillinger`),
        ]);
        setAnsatte(await ansatteRes.json());
        setInnstilling(await innstillingRes.json());
      } catch {
        console.error("Kunne ikke hente lønnskost");
      } finally {
        setLoading(false);
      }
    }
    hent();
  }, []);

  const manedsTotaler = useMemo(() => {
    if (!innstilling) return [];
    return MONTH_NAMES_LONN.map((navn, idx) => ({
      navn,
      idx,
      ...summerManed(ansatte, year, idx, innstilling),
    }));
  }, [ansatte, year, innstilling]);

  const arsTotal = useMemo(
    () => manedsTotaler.reduce((s, m) => s + m.total, 0),
    [manedsTotaler]
  );

  if (loading || !innstilling) {
    return <p className="text-center text-gray-500 py-10">Laster inn lønnskost…</p>;
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Lønnskost</h3>
          <p className="text-sm text-gray-500">Reell månedlig lønnskostnad inkl. arbeidsgiveravgift, feriepenger og OTP.</p>
        </div>
        <button
          onClick={() => setShowNewForm((v) => !v)}
          className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 self-start md:self-auto"
        >
          + Ny lønnsansatt
        </button>
      </div>

      <InnstillingerPanel innstilling={innstilling} onSaved={setInnstilling} />

      {showNewForm && (
        <NyAnsattForm
          onCreated={(a) => { setAnsatte((prev) => [...prev, a].sort((x, y) => x.navn.localeCompare(y.navn))); setShowNewForm(false); }}
          onCancel={() => setShowNewForm(false)}
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {ansatte.length === 0 ? (
          <p className="text-gray-400 col-span-full text-center py-6">Ingen lønnsansatte registrert ennå.</p>
        ) : (
          ansatte.map((a) => (
            <AnsattCard
              key={a._id}
              ansatt={a}
              year={year}
              monthIndex={detaljMonth}
              innstilling={innstilling}
              onUpdated={(oppdatert) => setAnsatte((prev) => prev.map((x) => (x._id === oppdatert._id ? oppdatert : x)))}
              onDeleted={(id) => setAnsatte((prev) => prev.filter((x) => x._id !== id))}
            />
          ))
        )}
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-lg font-semibold text-gray-900">Lønnskost pr. måned</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setYear((y) => y - 1)} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm">←</button>
          <span className="text-sm font-medium w-14 text-center">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm">→</button>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Måned</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Brutto</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Feriepenger</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">OTP</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Arb.giveravgift</th>
              <th className="px-3 py-2 text-right font-medium text-gray-900">Totalkostnad</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {manedsTotaler.map((m) => (
              <tr
                key={m.idx}
                onClick={() => setDetaljMonth(m.idx)}
                className={`cursor-pointer hover:bg-indigo-50 ${detaljMonth === m.idx ? "bg-indigo-50" : ""}`}
              >
                <td className="px-3 py-2 whitespace-nowrap">{m.navn}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{fmtKr(m.brutto)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{fmtKr(m.feriepenger)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{fmtKr(m.otp)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{fmtKr(m.arbeidsgiveravgift)}</td>
                <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{fmtKr(m.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr>
              <td className="px-3 py-2 font-semibold">Sum {year}</td>
              <td colSpan={4}></td>
              <td className="px-3 py-2 text-right font-bold text-gray-900">{fmtKr(arsTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-2">Klikk en månedsrad for å vise kostnaden pr. ansatt for den måneden over.</p>
    </div>
  );
}
